import { CommonModule } from '@angular/common';
import { Component, effect, inject, signal, untracked } from '@angular/core';
import {
  form,
  FormField,
  validateStandardSchema,
} from '@angular/forms/signals';

import { RouterModule } from '@angular/router';

// local
import {
  Card,
  FormErrorWrapper,
  FormLabel,
  PasswordInput,
} from '@aurrica-web/ui';
import { injectDispatch } from '@ngrx/signals/events';
import {
  PasswordResetConfirmation,
  PasswordResetConfirmationSchema,
  authEvents,
  AuthStore,
} from '@aurrica-web/core-data';
import { AuthFormHeader } from '../layout/auth-form-header';

@Component({
  selector: 'lib-auth-reset-confirmation-form',
  imports: [
    CommonModule,
    RouterModule,
    FormField,
    Card,
    PasswordInput,
    FormLabel,
    FormErrorWrapper,
    AuthFormHeader,
  ],
  template: `
    <lib-ui-card class="bg-white dark:bg-background-dark shadow-md rounded-md">
      <lib-auth-form-header title="Confirm Password Reset" />
      <form
        (submit)="onSubmit($event)"
        autocomplete="off"
        novalidate
        class="p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 items-start"
      >
        <div class="flex flex-col gap-0.5 col-span-1 sm:col-span-2">
          <lib-ui-form-error
            [form]="confirmationForm"
            ctrlName="code"
            #codeError="formError"
          >
            <lib-ui-form-label label="Confirmation Code" />
          </lib-ui-form-error>
          <input
            type="text"
            class="form-control"
            placeholder="182343"
            [ngClass]="{
              'form-control-valid': !codeError.hasError,
              'form-control-error': codeError.hasError,
            }"
            [formField]="confirmationForm.code"
            id="code"
          />
        </div>
        <div class="flex flex-col gap-0.5 col-span-1 sm:col-span-2">
          <lib-ui-form-error
            [form]="confirmationForm"
            ctrlName="email"
            #emailError="formError"
          >
            <lib-ui-form-label label="Email" />
          </lib-ui-form-error>
          <input
            type="email"
            id="email"
            placeholder="you@example.com"
            class="form-control"
            [ngClass]="{
              'form-control-valid': !emailError.hasError,
              'form-control-error': emailError.hasError,
            }"
            [formField]="confirmationForm.email"
          />
        </div>
        <div class="flex flex-col gap-0.5 col-span-1 sm:col-span-2">
          <lib-ui-form-error
            [form]="confirmationForm"
            ctrlName="newPassword"
            #newPasswordError="formError"
          >
            <lib-ui-form-label label="New Password" />
          </lib-ui-form-error>
          <lib-ui-password-input [formField]="confirmationForm.newPassword" />
        </div>

        <div class="flex flex-col gap-0.5 col-span-1 sm:col-span-2">
          <lib-ui-form-error
            [form]="confirmationForm"
            ctrlName="confirm_password"
            #confirmError="formError"
          >
            <lib-ui-form-label label="Confirm Password" />
          </lib-ui-form-error>
          <lib-ui-password-input
            [formField]="confirmationForm.confirm_password"
          />
        </div>

        <!-- Submit Button -->
        <div
          class="flex flex-row justify-center gap-2 col-span-1 sm:col-span-2 mt-4"
        >
          <button
            type="submit"
            class="text-sm px-4 py-1 rounded-md
            bg-slate-950 text-white cursor-pointer
            dark:bg-white dark:text-slate-950
            hover:bg-slate-800 dark:hover:bg-slate-200
            "
            [disabled]="confirmationForm().invalid()"
          >
            Reset
          </button>
        </div>
        <p
          class="text-gray-800 dark:text-gray-400 text-xs fonr-medium text-center mt-6 col-span-1 sm:col-span-2"
        >
          <a
            routerLink="/login"
            class="text-blue-500 font-semibold hover:underline ml-1 whitespace-nowrap"
            >Sign In</a
          >
        </p>
      </form>
    </lib-ui-card>
  `,
})
export class PasswordResetConfirmationForm {
  authStore = inject(AuthStore);
  readonly dispatch = injectDispatch(authEvents);
  confirmationModel = signal<PasswordResetConfirmation>({
    email: '',
    code: '',
    newPassword: '',
    confirm_password: '',
  });
  confirmationForm = form(this.confirmationModel, (schemaPath) =>
    validateStandardSchema(schemaPath, PasswordResetConfirmationSchema),
  );

  syncFormEffect = effect(() => {
    const email = this.authStore.confirmationEmail();

    untracked(() => {
      if (email && email !== this.confirmationForm().value().email) {
        this.confirmationModel.update((current) => ({ ...current, email }));
      }
    });
  });

  onSubmit(event?: Event) {
    event?.preventDefault();
    if (this.confirmationForm().valid()) {
      const { code, email, newPassword } = this.confirmationForm().value();
      if (code && email && newPassword) {
        this.dispatch.confirmPasswordReset({
          code: code,
          email,
          newPassword,
        });
      }
    } else {
      this.confirmationForm.code().markAsTouched();
      this.confirmationForm.email().markAsTouched();
      this.confirmationForm.newPassword().markAsTouched();
      this.confirmationForm.confirm_password().markAsTouched();
    }
  }
}
